/***********************************************************************************
 * Refund View
 *
 * @author Zip Co - Plugin Team
 *
 ***********************************************************************************/

import { mapGetters, mapActions } from 'vuex';
import { Component, Vue } from 'vue-property-decorator';

import Spinner from '@/components/Spinner';
import config from '@/config/client';
import Token from '@/models/Token';

@Component({
    metaInfo(): object {
        return {
            title: config.views.refund.title
        };
    },
    computed: {
        ...mapGetters({
            isAuth: 'isAuth',
            origin: 'getOrigin',
            registerId: 'getRegisterId',
            token: 'getToken'
        })
    },
    methods: {
        ...mapActions({
            setCloseButton: 'setCloseButton'
        })
    },
    components: {
        Spinner
    }
})
class Refund extends Vue {
    public $http: any;
    public $router: any;

    private success: boolean = false;
    private loading: boolean = true;
    private error: boolean = false;
    private message: string = config.views.refund.defaultMessage;
    private successMessage: string = config.views.refund.successMessage;

    private logoUrl: string = config.views.refund.logo;
    private logoLabel: string = 'Zip';
    private confirmButtonEnabled: boolean = false;

    private isAuth!: boolean;
    private origin!: string;
    private registerId!: string;
    private saleDetail: any = false;
    private token!: Token;

    private setCloseButton!: (enabled: boolean) => Promise<void>;

    public created(): void {
        if (!this.isAuth) {
            this.$router.push({
                name: 'pairing'
            });
        }
    }

    public mounted(): void {
        this.setupMessageEventListener();
        this.requestRefundData();
    }

    /**
     * get refund amount
     */
    private get amount(): number {
        return this.saleDetail ? this.saleDetail.payment.amount * -1 : 0;
    }

    /**
     * set up event listener for message response
     */
    private setupMessageEventListener(): void {
        window.addEventListener(
            'message',
            async (event): Promise<void> => {
                this.saleDetail = JSON.parse(event.data);

                if (this.saleDetail.payment.external_transaction_id) {
                    this.confirmButtonEnabled = true;
                    this.loading = false;
                } else {
                    this.setError(config.views.refund.unableRefundErrorMessage);
                    this.toggleCloseButton(true);
                }
            },
            false
        );
    }

    /**
     * process refund
     */
    private async processRefund(): Promise<void> {
        this.toggleCloseButton(false);
        this.loading = true;

        try {
            const { data: success } = await this.$http.post('/api/refund/create', {
                saleDetail: this.saleDetail,
                token: this.token
            });

            if (success) {
                this.completeTransaction();
                this.loading = false;
            } else {
                this.declineTransaction();
            }
        } catch (error) {
            this.setError(error.response.data.message || error.message);
            this.toggleCloseButton(true);
        }
    }

    /**
     * Show or hide close button
     * @param enabled
     */
    private toggleCloseButton(enabled: boolean = true): void {
        this.setCloseButton(enabled);
        window.parent.postMessage(
            JSON.stringify({
                step: 'SETUP',
                setup: {
                    enable_close: enabled
                }
            }),
            this.origin
        );
    }

    /**
     * request refund data from Vend
     */
    private requestRefundData(): void {
        const message = JSON.stringify({
            step: 'DATA'
        });

        try {
            window.parent.postMessage(message, this.origin);
        } catch (e) {
            this.loading = false;
            this.setError(config.views.refund.exceptionMessage);
            this.toggleCloseButton(true);
        }
    }

    /**
     * Handle complete refund
     */
    private completeTransaction(): void {
        this.success = true;

        setTimeout((): void => {
            const data = {
                step: 'ACCEPT',
                receipt_html_extra: config.views.refund.receiptHtml
            };

            window.parent.postMessage(JSON.stringify(data), this.origin);
            this.toggleCloseButton(true);
        }, config.views.delayBeforeReturn);
    }

    /**
     * Handle declined Transaction
     */
    private declineTransaction(): void {
        setTimeout((): void => {
            const data = {
                step: 'DECLINE',
                transaction_id: this.registerId
            };
            window.parent.postMessage(JSON.stringify(data), this.origin);
            this.toggleCloseButton(true);
        }, config.views.delayBeforeReturn);
    }

    /**
     * Set error and error message
     * @param message
     */
    private setError(message: string): void {
        this.error = true;
        this.message = message;
    }
}

export default Refund;
